import { ApolloError } from '@apollo/client';

import { AppointmentType } from 'kb-shared/types';

import { useAppointment } from './useAppointment';
import { useAppointmentType } from './useAppointmentType';
import { useAppointmentTypes } from './useAppointmentTypes';
import { useDefaultLab } from './useDefaultLab';
import { useLab } from './useLab';
import { useLocation } from './useLocation';
import { useTimeSlot } from './useTimeSlot';
import { LoadedEntities } from '../types/types';
import { NormalizedBookingSearchParams } from '../types/types';
import { containsOnlyVirtualAppointmentType, isVirtualAppointmentType } from '../utils/validation';

export type LoadResult = {
  loading: boolean;
  error: ApolloError | undefined;
  loadedEntities: Partial<LoadedEntities>;
};

export const useLoadEntities = ({
  appointmentId,
  appointmentTypeIds,
  labId,
  clinicId,
  timeSlotId,
  confirmedAppointmentId
}: NormalizedBookingSearchParams): LoadResult => {
  const responseAppointmentType = useAppointmentType(appointmentId, labId);
  const responseAppointmentTypes = useAppointmentTypes(appointmentTypeIds, labId);
  const responseConfirmedAppointment = useAppointment(confirmedAppointmentId);
  const responseLab = useLab(labId);
  const responseDefaultLab = useDefaultLab({ skip: Boolean(labId) });
  const responseLocation = useLocation(clinicId);
  const responseTimeSlot = useTimeSlot(timeSlotId, appointmentId);

  const loading =
    responseAppointmentType.loading ||
    responseAppointmentTypes.loading ||
    responseConfirmedAppointment.loading ||
    responseLab.loading ||
    responseDefaultLab.loading ||
    responseLocation.loading ||
    responseTimeSlot.loading;

  const error =
    responseAppointmentType.error ||
    responseAppointmentTypes.error ||
    responseConfirmedAppointment.error ||
    responseLab.error ||
    responseDefaultLab.error ||
    responseLocation.error ||
    responseTimeSlot.error;

  if (loading) {
    return { loading, error: error, loadedEntities: {} };
  }

  const loadedEntities: Partial<LoadedEntities> = {};
  const labData = responseLab.data || responseDefaultLab.data;

  loadedEntities.allAppointmentTypes = (loadedEntities.selectedAppointmentTypes || []).concat(
    responseAppointmentType.data
      ? [
          responseAppointmentType.data,
          ...(responseAppointmentType.data.inClinicOrVirtualEquivalent
            ? [responseAppointmentType.data.inClinicOrVirtualEquivalent]
            : [])
        ]
      : []
  );

  const virtualAppointmentType = loadedEntities.allAppointmentTypes.find(isVirtualAppointmentType);
  const isOnlyVirtual =
    containsOnlyVirtualAppointmentType(loadedEntities.allAppointmentTypes) &&
    !loadedEntities.linkedAppointmentType?.virtual;

  if (virtualAppointmentType && isOnlyVirtual && !loadedEntities.selectedProduct) {
    loadedEntities.selectedProduct = {
      type: 'appointment',
      data: virtualAppointmentType
    };
  }

  const selectedProduct = getSelectedProduct(
    loadedEntities.allAppointmentTypes,
    responseTimeSlot.data
  );

  if (responseAppointmentType.data) {
    loadedEntities.selectedProduct = {
      type: 'appointment',
      data: selectedProduct ?? responseAppointmentType.data
    };
    if (responseAppointmentType.data.inClinicOrVirtualEquivalent) {
      loadedEntities.linkedAppointmentType =
        responseAppointmentType.data.inClinicOrVirtualEquivalent;
    }
  }
  if (responseAppointmentTypes.data) {
    loadedEntities.selectedAppointmentTypes = responseAppointmentTypes.data;
  }

  if (labData) {
    loadedEntities.selectedLab = labData;
  }
  if (responseLocation.data) {
    loadedEntities.selectedClinic = responseLocation.data;
  }
  if (responseTimeSlot.data) {
    loadedEntities.selectedTimeSlot = responseTimeSlot.data;
  }
  if (responseConfirmedAppointment.data) {
    loadedEntities.purchasedProduct = {
      type: 'appointment',
      data: responseConfirmedAppointment.data
    };
  }

  return {
    loading,
    error,
    loadedEntities
  };
};

const getSelectedProduct = (
  allAppointmentTypes: AppointmentType[] = [],
  timeSlotData?: { virtualAppointment?: boolean }
): AppointmentType | undefined => {
  if (!timeSlotData) return undefined;
  const isVirtual = timeSlotData.virtualAppointment;
  return allAppointmentTypes.find(appointmentType => appointmentType.virtual === isVirtual);
};
